/**
 * Custom hook for drag and drop file functionality
 */

import { useState, useCallback, useEffect, useRef, DragEvent } from 'react';

interface UseDragAndDropOptions {
  onDrop?: (files: File[]) => void;
  acceptedFileTypes?: string[];
  multiple?: boolean;
  disabled?: boolean;
  /**
   * Ignore drags that carry no file, leaving the event entirely untouched (VALE-1592).
   *
   * Off by default, because a drop surface is normally a small box the reader deliberately
   * aimed at: whatever they released on it they meant for the upload, so swallowing the
   * gesture is right even when nothing usable came with it.
   *
   * Turn it on when the element covering the gesture is *large* — a whole dialog, a whole
   * pane. There, a drag that is not a file drop far more likely belongs to a control inside
   * it (text dragged between two inputs, a native reorder) than to a misaimed upload. Every
   * handler below then returns before touching the event, so it neither `preventDefault`s
   * nor `stopPropagation`s and the inner control keeps its own behaviour.
   */
  filesOnly?: boolean;
}

/**
 * Whether a drag carries at least one file.
 *
 * `types` is the only part of `dataTransfer` readable during `dragenter`/`dragover` — the
 * items themselves are protection-mode-locked until `drop` — so it is what a mid-drag
 * decision has to be made on.
 */
function carriesFiles(e: DragEvent<HTMLElement>): boolean {
  return Array.from(e.dataTransfer?.types ?? []).includes('Files');
}

interface UseDragAndDropReturn {
  // Drag state
  isDragging: boolean;

  // Event handlers
  dragHandlers: {
    onDragEnter: (e: DragEvent<HTMLElement>) => void;
    onDragLeave: (e: DragEvent<HTMLElement>) => void;
    onDragOver: (e: DragEvent<HTMLElement>) => void;
    onDrop: (e: DragEvent<HTMLElement>) => void;
  };

  // Utilities
  getRootProps: () => {
    onDragEnter: (e: DragEvent<HTMLElement>) => void;
    onDragLeave: (e: DragEvent<HTMLElement>) => void;
    onDragOver: (e: DragEvent<HTMLElement>) => void;
    onDrop: (e: DragEvent<HTMLElement>) => void;
  };
}

export function useDragAndDrop({
  onDrop,
  acceptedFileTypes,
  multiple = true,
  disabled = false,
  filesOnly = false
}: UseDragAndDropOptions = {}): UseDragAndDropReturn {
  const [isDragging, setIsDragging] = useState(false);
  const dragCounterRef = useRef(0);

  /**
   * Reset when a drag ends anywhere but inside the element (VALE-597).
   *
   * `isDragging` is derived from an enter/leave counter, which only balances while every
   * event pairs up on the element. Two ordinary gestures break that: releasing the file
   * over browser chrome, and dragging back out of the window — in both, the element's own
   * `drop` handler never fires, so the count stays above zero and whatever the consumer
   * renders on `isDragging` (an overlay, a highlighted border) never dismisses.
   *
   * `dragend` and `drop` on the window cover both, and are the events the browser fires
   * regardless of where the gesture finished. Registered in the capture phase so a
   * `stopPropagation` on the element's own handler cannot suppress the safety net; both
   * paths settle on the same state, so the double reset is harmless.
   *
   * The counter is reset alongside the flag, not just the flag: clearing the boolean on its
   * own would leave a stale count, and the next drag's first `dragleave` could then never
   * bring it back to zero.
   */
  useEffect(() => {
    const reset = () => {
      dragCounterRef.current = 0;
      setIsDragging(false);
    };
    window.addEventListener('dragend', reset, true);
    window.addEventListener('drop', reset, true);
    return () => {
      window.removeEventListener('dragend', reset, true);
      window.removeEventListener('drop', reset, true);
    };
  }, []);

  // Filter files based on accepted types
  const filterFiles = useCallback(
    (files: FileList): File[] => {
      const fileArray = Array.from(files);

      if (!acceptedFileTypes || acceptedFileTypes.length === 0) {
        return multiple ? fileArray : fileArray.slice(0, 1);
      }

      const filtered = fileArray.filter((file) => {
        const extension = file.name.split('.').pop()?.toLowerCase();
        if (!extension) return false;
        return acceptedFileTypes.some(
          (type) => type.toLowerCase().replace(/^\./, '') === extension
        );
      });

      return multiple ? filtered : filtered.slice(0, 1);
    },
    [acceptedFileTypes, multiple]
  );

  // Handle drag enter
  const handleDragEnter = useCallback(
    (e: DragEvent<HTMLElement>) => {
      if (filesOnly && !carriesFiles(e)) return;
      e.preventDefault();
      e.stopPropagation();

      if (disabled) return;

      dragCounterRef.current++;

      if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
        setIsDragging(true);
      }
    },
    [disabled, filesOnly]
  );

  // Handle drag leave
  const handleDragLeave = useCallback(
    (e: DragEvent<HTMLElement>) => {
      if (filesOnly && !carriesFiles(e)) return;
      e.preventDefault();
      e.stopPropagation();

      if (disabled) return;

      dragCounterRef.current--;

      if (dragCounterRef.current === 0) {
        setIsDragging(false);
      }
    },
    [disabled, filesOnly]
  );

  // Handle drag over
  const handleDragOver = useCallback(
    (e: DragEvent<HTMLElement>) => {
      if (filesOnly && !carriesFiles(e)) return;
      e.preventDefault();
      e.stopPropagation();

      if (disabled) return;

      // Set drop effect
      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = 'copy';
      }
    },
    [disabled, filesOnly]
  );

  // Handle drop
  const handleDrop = useCallback(
    (e: DragEvent<HTMLElement>) => {
      if (filesOnly && !carriesFiles(e)) return;
      e.preventDefault();
      e.stopPropagation();

      if (disabled) return;

      setIsDragging(false);
      dragCounterRef.current = 0;

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        const files = filterFiles(e.dataTransfer.files);

        if (files.length > 0 && onDrop) {
          onDrop(files);
        }

        // Clear the data transfer
        e.dataTransfer.clearData();
      }
    },
    [disabled, filesOnly, filterFiles, onDrop]
  );

  // Get root props for easy spreading
  const getRootProps = useCallback(
    () => ({
      onDragEnter: handleDragEnter,
      onDragLeave: handleDragLeave,
      onDragOver: handleDragOver,
      onDrop: handleDrop
    }),
    [handleDragEnter, handleDragLeave, handleDragOver, handleDrop]
  );

  return {
    isDragging,
    dragHandlers: {
      onDragEnter: handleDragEnter,
      onDragLeave: handleDragLeave,
      onDragOver: handleDragOver,
      onDrop: handleDrop
    },
    getRootProps
  };
}

/**
 * Hook for file input management
 */
export function useFileInput(
  onChange: (files: File[]) => void,
  options: {
    multiple?: boolean;
    accept?: string;
  } = {}
) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleClick = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        onChange(Array.from(files));
        // Reset input value to allow selecting the same file again
        e.target.value = '';
      }
    },
    [onChange]
  );

  const getInputProps = useCallback(
    () => ({
      ref: inputRef,
      type: 'file' as const,
      style: { display: 'none' },
      multiple: options.multiple,
      accept: options.accept,
      onChange: handleChange
    }),
    [options.multiple, options.accept, handleChange]
  );

  return {
    inputRef,
    openFileDialog: handleClick,
    getInputProps
  };
}
